import { SeqType, colorForResidue, computeColumnData, detectSeqType } from './logo-render';

const FONT_SIZE = 12;
const CHAR_BASE_WIDTH = 8;
const MIN_LETTER_HEIGHT = 1;
// Fraction of FONT_SIZE occupied by uppercase cap height (Arial/Helvetica ~0.72).
// We scale text so the visible glyph – not the full em-box – fills the slot.
const CAP_RATIO = 0.72;
// Match the left/right inset used by all nightingale-new-core components
// (viewport = [margin-left, width - margin-right]).
const MARGIN_LEFT = 10;
const MARGIN_RIGHT = 10;

class LogoViewer extends HTMLElement {
  private _sequences: Array<{ name: string; sequence: string }> = [];
  private _seqType: SeqType = 'rna';
  private _resizeObserver?: ResizeObserver;

  static get observedAttributes() {
    return ['display-start', 'display-end', 'length', 'height'];
  }

  connectedCallback() {
    this._resizeObserver = new ResizeObserver(() => {
      if (this._sequences.length > 0) this.renderLogo();
    });
    this._resizeObserver.observe(this);

    // Register with the parent nightingale-manager so it propagates
    // display-start / display-end changes to this element.
    requestAnimationFrame(() => {
      const manager = this.closest('nightingale-manager');
      if (manager) (manager as any).register(this);
    });
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    const manager = this.closest('nightingale-manager');
    if (manager) (manager as any).unregister(this);
  }

  attributeChangedCallback() {
    if (this._sequences.length > 0) this.renderLogo();
  }

  set sequences(value: Array<{ name: string; sequence: string }>) {
    this._sequences = value;
    this._seqType = detectSeqType(value);
    this.renderLogo();
  }

  private get displayStart(): number {
    return parseInt(this.getAttribute('display-start') || '1');
  }

  private get displayEnd(): number {
    const raw = parseInt(this.getAttribute('display-end') ?? '');
    if (isNaN(raw) || raw < 0) {
      return parseInt(this.getAttribute('length') ?? '') || this.displayStart;
    }
    return raw;
  }

  private get trackHeight(): number {
    return parseInt(this.getAttribute('height') || '80');
  }

  private renderLogo(): void {
    const width = this.clientWidth;
    const height = this.trackHeight;
    if (width <= 0 || height <= 0) return;

    const start = this.displayStart;
    const end = this.displayEnd;
    const numCols = end - start + 1;
    if (numCols <= 0) return;

    const seqWidth = width - MARGIN_LEFT - MARGIN_RIGHT;
    const colWidth = seqWidth / numCols;
    const parts: string[] = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" style="display:block;">`
    ];

    for (let pos = start; pos <= end; pos++) {
      const colData = computeColumnData(this._sequences, this._seqType, pos);
      const xLeft = MARGIN_LEFT + (pos - start) * colWidth;
      const centerX = xLeft + colWidth / 2;
      let stackY = height;

      for (const item of colData) {
        const letterHeight = item.heightFraction * height;
        if (letterHeight < MIN_LETTER_HEIGHT) continue;

        stackY -= letterHeight;
        const scaleX = colWidth / CHAR_BASE_WIDTH;
        // Scale so the visible cap height (not the full em-box) fills letterHeight.
        const scaleY = letterHeight / (FONT_SIZE * CAP_RATIO);
        const color = colorForResidue(item.nucleotide, this._seqType);
        // Anchor at the alphabetic baseline = bottom of the allocated slot.
        // The cap then extends exactly up to stackY with no gap.
        const baselineY = stackY + letterHeight;

        parts.push(
          `<text ` +
          `transform="translate(${centerX.toFixed(2)},${baselineY.toFixed(2)}) scale(${scaleX.toFixed(3)},${scaleY.toFixed(3)})" ` +
          `text-anchor="middle" ` +
          `font-family="Arial,Helvetica,sans-serif" font-size="${FONT_SIZE}" font-weight="bold" ` +
          `fill="${color}">${item.nucleotide}</text>`
        );
      }
    }

    // Draw margin overlays to mask letters that extend into margin areas
    const marginColor = getComputedStyle(this).backgroundColor || '#ffffff';
    parts.push(
      `<rect x="0" y="0" width="${MARGIN_LEFT}" height="${height}" fill="${marginColor}" pointer-events="none"/>`,
      `<rect x="${width - MARGIN_RIGHT}" y="0" width="${MARGIN_RIGHT}" height="${height}" fill="${marginColor}" pointer-events="none"/>`
    );

    parts.push('</svg>');
    this.innerHTML = parts.join('');
  }
}

if (!customElements.get('logo-viewer')) {
  customElements.define('logo-viewer', LogoViewer);
}
